import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import { useLanguage } from "@/lib/LanguageContext";
import { resolveImageUrl } from "@/lib/media";
import {
  ChevronLeft,
  Calendar,
  Tag,
  Share2,
  Printer,
  Loader2,
  AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsDetail = () => {
  const { id } = useParams();
  const { lang } = useLanguage();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allNews, setAllNews] = useState([]);

  // Fetch the full news list to derive "related" articles.
  useEffect(() => {
    axios
      .get(`${API}/news`)
      .then((r) => setAllNews(r.data || []))
      .catch(() => setAllNews([]));
  }, [id]);

  const related = useMemo(() => {
    if (!item) return [];
    const others = allNews.filter((n) => n.id !== item.id);
    const sameCat = others.filter(
      (n) => n.category_id && item.category_id && n.category_id === item.category_id
    );
    const sameIds = new Set(sameCat.map((n) => n.id));
    const rest = others.filter((n) => !sameIds.has(n.id));
    return [...sameCat, ...rest].slice(0, 3);
  }, [allNews, item]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/news/${id}`)
      .then((r) => setItem(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "not_found" : "error"))
      .finally(() => setLoading(false));
  }, [id]);

  // Ensure the article opens scrolled to the very top (React Router keeps the
  // previous scroll position, which made the detail page appear at the bottom).
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [id]);

  // Dynamically update <title> and og:image / twitter:image meta so that
  // social scrapers that DO execute JS (or when re-scraping via debugger tools)
  // see the article-specific branded preview image.
  useEffect(() => {
    if (!item) return;
    const title = (lang === "id" ? item.title_id : item.title_en) || item.title_id;
    const origTitle = document.title;
    if (title) document.title = `${title} — RS Bhayangkara Tarakan`;
    const ogUrl = `${API}/og-image?news_id=${encodeURIComponent(item.id)}&lang=${lang}`;
    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [k, v] = selector.replace("meta[", "").replace("]", "").split("=");
        el.setAttribute(k, v.replace(/"/g, ""));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    setMeta('meta[property="og:image"]', "content", ogUrl);
    setMeta('meta[name="twitter:image"]', "content", ogUrl);
    if (title) {
      setMeta('meta[property="og:title"]', "content", title);
      setMeta('meta[name="twitter:title"]', "content", title);
    }
    return () => {
      document.title = origTitle;
    };
  }, [item, lang]);

  const handleShare = async () => {
    if (!item) return;
    const url = window.location.href;
    const title = lang === "id" ? item.title_id : item.title_en;
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch {
        /* user cancelled */
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        alert("Link disalin ke clipboard");
      } catch {
        /* ignore */
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" data-testid="news-detail-page">
      <div className="no-print">
        <Header />
      </div>
      <main className="flex-1 pt-24 pb-16 print:pt-4">
        <div className="container-x max-w-4xl">
          {loading && (
            <div className="p-12 flex items-center justify-center text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}

          {!loading && error === "not_found" && (
            <div
              data-testid="news-detail-not-found"
              className="p-8 rounded-3xl bg-amber-50 border border-amber-200 text-amber-800 text-center"
            >
              <AlertTriangle className="h-8 w-8 mx-auto mb-3" />
              <div className="font-heading font-bold text-lg mb-2">
                Berita tidak ditemukan
              </div>
              <div className="text-sm">
                Artikel yang Anda cari tidak tersedia atau telah dihapus.
              </div>
              <Link
                to="/"
                data-testid="news-detail-back-home"
                className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-full bg-blue-800 hover:bg-blue-900 text-white text-sm font-medium"
              >
                <ChevronLeft className="h-4 w-4" />
                Kembali ke Beranda
              </Link>
            </div>
          )}

          {!loading && !error && item && (
            <>
              {/* Top navigation */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6 no-print">
                <Link
                  to="/#news"
                  data-testid="news-detail-back-link"
                  className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-blue-800"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Kembali ke Berita
                </Link>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleShare}
                    data-testid="news-detail-share"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700"
                  >
                    <Share2 className="h-3.5 w-3.5" />
                    Bagikan
                  </button>
                  <button
                    onClick={() => window.print()}
                    data-testid="news-detail-print"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700"
                  >
                    <Printer className="h-3.5 w-3.5" />
                    Cetak
                  </button>
                </div>
              </div>

              {/* Cover image */}
              <figure className="rounded-3xl overflow-hidden border border-slate-100 shadow-sm bg-white mb-6">
                <div className="aspect-[16/9] bg-slate-100">
                  <img
                    src={resolveImageUrl(item.image)}
                    alt={lang === "id" ? item.title_id : item.title_en}
                    className="w-full h-full object-cover"
                  />
                </div>
              </figure>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-4">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-800 font-medium">
                  <Tag className="h-3 w-3" />
                  {lang === "id" ? item.category_id : item.category_en}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {item.date}
                </span>
              </div>

              {/* Title */}
              <h1
                data-testid="news-detail-title"
                className="font-heading font-bold text-3xl md:text-4xl text-slate-900 leading-tight mb-4"
              >
                {lang === "id" ? item.title_id : item.title_en}
              </h1>

              {/* Article body — excerpt used as summary since no long content field */}
              <div className="prose prose-slate max-w-none">
                <p
                  data-testid="news-detail-excerpt"
                  className="text-lg text-slate-700 leading-relaxed whitespace-pre-wrap"
                >
                  {lang === "id" ? item.excerpt_id : item.excerpt_en}
                </p>
              </div>

              {/* Contact block */}
              <div className="mt-10 p-5 rounded-2xl bg-blue-50 border border-blue-100 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-1">
                  <div className="font-heading font-semibold text-slate-900">
                    Butuh informasi lebih lanjut?
                  </div>
                  <div className="text-sm text-slate-600 mt-0.5">
                    Hubungi Humas RS Bhayangkara Tarakan
                  </div>
                </div>
                <a
                  href="https://wa.me/6282241761118"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="news-detail-cta-whatsapp"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-emerald-500 hover:bg-emerald-600 text-white transition"
                >
                  Hubungi WhatsApp
                </a>
              </div>

              {/* Related news */}
              {related.length > 0 && (
                <div className="mt-12 no-print" data-testid="news-related">
                  <h2 className="font-heading font-bold text-2xl text-slate-900 mb-5">
                    {lang === "id" ? "Berita Terkait" : "Related News"}
                  </h2>
                  <div className="grid sm:grid-cols-3 gap-5">
                    {related.map((n) => (
                      <Link
                        key={n.id}
                        to={`/berita/${n.id}`}
                        data-testid={`related-news-${n.id}`}
                        className="group bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-md hover:border-blue-200 transition"
                      >
                        <div className="aspect-video overflow-hidden bg-slate-100">
                          <img
                            src={resolveImageUrl(n.image)}
                            alt={lang === "id" ? n.title_id : n.title_en}
                            className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-4">
                          <div className="text-xs text-slate-400 mb-1 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {n.date}
                          </div>
                          <div className="font-heading font-semibold text-slate-900 text-sm line-clamp-2 group-hover:text-blue-800">
                            {lang === "id" ? n.title_id : n.title_en}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <div className="no-print">
        <Footer />
      </div>
      <div className="no-print">
        <FloatingWhatsApp />
      </div>
    </div>
  );
};

export default NewsDetail;
